import { sceneToScreen } from "./viewportTransform";

export const SELECTION_HANDLE_SIZE = 10;
export const SELECTION_HANDLE_HIT_PADDING = 4;

export const RESIZE_HANDLE_IDS = Object.freeze([
  "north-west",
  "north",
  "north-east",
  "east",
  "south-east",
  "south",
  "south-west",
  "west",
]);

const RESIZE_CURSORS = Object.freeze({
  east: "ew-resize",
  north: "ns-resize",
  "north-east": "nesw-resize",
  "north-west": "nwse-resize",
  south: "ns-resize",
  "south-east": "nwse-resize",
  "south-west": "nesw-resize",
  west: "ew-resize",
});

/**
 * @param {{ bounds: { x: number, y: number, width: number, height: number }, viewport: object }} params
 * @returns {{ x: number, y: number, width: number, height: number }} screen-space box
 */
export const getScreenBounds = ({ bounds, viewport }) => {
  const topLeft = sceneToScreen({ point: bounds, viewport });

  return {
    height: bounds.height * viewport.zoom,
    width: bounds.width * viewport.zoom,
    x: topLeft.x,
    y: topLeft.y,
  };
};

/**
 * Resize handle centers in screen space. Handle size stays constant on screen
 * regardless of zoom, like Excalidraw.
 * @param {{ screenBounds: { x: number, y: number, width: number, height: number } }} params
 * @returns {Array<{ id: string, x: number, y: number }>}
 */
export const getResizeHandleCenters = ({ screenBounds }) => {
  const middleX = screenBounds.x + screenBounds.width / 2;
  const middleY = screenBounds.y + screenBounds.height / 2;
  const east = screenBounds.x + screenBounds.width;
  const south = screenBounds.y + screenBounds.height;

  return [
    { id: "north-west", x: screenBounds.x, y: screenBounds.y },
    { id: "north", x: middleX, y: screenBounds.y },
    { id: "north-east", x: east, y: screenBounds.y },
    { id: "east", x: east, y: middleY },
    { id: "south-east", x: east, y: south },
    { id: "south", x: middleX, y: south },
    { id: "south-west", x: screenBounds.x, y: south },
    { id: "west", x: screenBounds.x, y: middleY },
  ];
};

const isPointNearHandleCenter = ({ center, screenPoint }) => {
  const reach = SELECTION_HANDLE_SIZE / 2 + SELECTION_HANDLE_HIT_PADDING;

  return (
    Math.abs(screenPoint.x - center.x) <= reach &&
    Math.abs(screenPoint.y - center.y) <= reach
  );
};

/**
 * @param {{ screenBounds: object, screenPoint: { x: number, y: number } }} params
 * @returns {string | null} handle id
 */
export const getResizeHandleAtPoint = ({ screenBounds, screenPoint }) =>
  getResizeHandleCenters({ screenBounds }).find((center) =>
    isPointNearHandleCenter({ center, screenPoint })
  )?.id ?? null;

/**
 * @param {{ arrowPoints: { x1: number, y1: number, x2: number, y2: number }, screenPoint: { x: number, y: number }, viewport: object }} params
 * @returns {"start" | "end" | null}
 */
export const getArrowEndpointAtPoint = ({ arrowPoints, screenPoint, viewport }) => {
  const start = sceneToScreen({
    point: { x: arrowPoints.x1, y: arrowPoints.y1 },
    viewport,
  });
  const end = sceneToScreen({
    point: { x: arrowPoints.x2, y: arrowPoints.y2 },
    viewport,
  });
  if (isPointNearHandleCenter({ center: start, screenPoint })) return "start";
  if (isPointNearHandleCenter({ center: end, screenPoint })) return "end";

  return null;
};

/**
 * @param {string} handleId
 * @returns {string} CSS cursor
 */
export const getResizeCursor = (handleId) =>
  RESIZE_CURSORS[handleId] ?? "default";
